import React, { ForwardedRef, ReactElement, ReactNode, useEffect } from 'react';

import {
    Box,
    Breakpoint,
    CloseIcon,
    Container,
    Drawer,
    DrawerProps,
    IconButton,
    Toolbar
} from '@methanesat/ui-components';
import { useTranslate } from '../../hooks';

type SizeRangeUp = 'xsUp' | 'smUp' | 'mdUp' | 'lgUp' | 'xlUp';

/**
 * Props for BelowNavDrawer. Limits anchor to left & right, requires onClose, adds showAppBar
 */
export interface BelowNavDrawerProps extends DrawerProps {
    'data-testid'?: string;
    anchor?: 'left' | 'right';
    children?: ReactNode;
    header?: ReactNode;
    onClose: Exclude<DrawerProps['onClose'], undefined>;
    /**
     * Sizes where the drawer should show under the app bar.
     * On smaller screens, the drawer will be full-height &  hide the app bar.
     */
    showAppBar?: SizeRangeUp;
    paperRef?: ForwardedRef<HTMLDivElement>;
    resetScrollOnChange?: string;
    open?: boolean;
}

/**
 * Full-height side drawer for map controls.
 * Under the app bar on larger screens,
 * truly full-height on mobile.
 */
const BelowNavDrawer = ({
    'data-testid': dataTestId,
    anchor = 'right',
    children,
    header,
    variant = 'persistent',
    showAppBar = 'smUp',
    paperRef,
    resetScrollOnChange,
    ...drawerProps
}: BelowNavDrawerProps): ReactElement => {
    const t = useTranslate();

    // Reset scroll when resetScrollOnChange is updated, otherwise content is updated and scroll position is not reset
    useEffect(() => {
        if (paperRef && 'current' in paperRef && paperRef.current) {
            paperRef.current.scrollTop = 0;
        }
    }, [resetScrollOnChange, paperRef]);

    return (
        <Drawer
            data-testid={dataTestId || 'side-drawer'}
            {...drawerProps}
            anchor={anchor}
            variant={variant}
            PaperProps={{
                ref: paperRef,
                sx: {
                    maxWidth: {
                        sm: '500px'
                    },
                    minWidth: {
                        xs: '100%',
                        sm: '400px'
                    },
                    width: {
                        xs: '100%',
                        sm: '40%'
                    },
                    zIndex: (theme) => {
                        return {
                            // on xs screens show drawer above app bar
                            xs: theme.zIndex.drawer + 2,
                            sm: theme.zIndex.drawer
                        };
                    }
                }
            }}
        >
            {/* empty toolbar to keep drawer under the app bar, and all drawer contents visible */}
            <Toolbar
                sx={(theme) => ({
                    display: 'none',
                    [theme.breakpoints.up(showAppBar.toLowerCase().slice(0, 2) as Breakpoint)]: {
                        display: 'block'
                    }
                })}
            />

            <Box position="relative" padding={1} paddingTop={0}>
                <Container
                    sx={(theme) => ({
                        display: 'flex',
                        width: 'stretch',
                        zIndex: 1,
                        marginX: `-${theme.spacing(1)}`,
                        '& > *': {
                            marginX: `-${theme.spacing(3)}`
                        }
                    })}
                >
                    {header ? (
                        header
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                width: 'stretch',
                                paddingX: 2,
                                paddingTop: 2,
                                marginBottom: -2
                            }}
                        >
                            <IconButton
                                data-testid="close-drawer-button"
                                aria-label={t(`emissionsMapPage.belowNavDrawer.accessibility.closeDrawerButton`)}
                                onClick={(ev) => drawerProps.onClose && drawerProps.onClose(ev, 'escapeKeyDown')}
                                sx={
                                    {
                                        // position: 'absolute',
                                        // right: (theme) => theme.spacing(2),
                                        // top: (theme) => theme.spacing(2)
                                    }
                                }
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    )}
                </Container>
                <Box sx={{ marginTop: 1 }}>{children}</Box>
            </Box>
        </Drawer>
    );
};

export default BelowNavDrawer;
